import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export const Stats: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({ revenue: 0, users: 0, hours: 0 });
  const statsRef = useRef<HTMLDivElement>(null);

  const stats = [
    {
      icon: TrendingUpIcon,
      value: 25,
      suffix: '+ milliárd Ft',
      title: 'árbevétel',
      description: 'WiseBox kontrolling rendszert használó Ügyfeleink éves nettó árbevétele 2024-ben meghaladta \na 25 milliárd Ft-ot',
      animationKey: 'revenue' as const,
    },
    {
      icon: GroupsIcon,
      value: 80,
      suffix: '+',
      title: 'ügyfél',
      description: 'Jelenleg több mint 80 Ügyfelünk szavazott bizalmat és használja a rendszerünket nap mint nap, és ez a szám folyamatosan növekszik',
      animationKey: 'users' as const,
    },
    {
      icon: AccessTimeIcon,
      value: 6700,
      suffix: '+',
      title: 'megtakarított óra',
      description: '3-5 főnél fél fő munkáját,\n5-10 főnél 1 fő munkáját,\n10+ főnél 1.5+ fő munkáját tudjuk megtakarítani hónapról hónapra',
      animationKey: 'hours' as const,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedValues({
        revenue: Math.floor(25 * progress),
        users: Math.floor(80 * progress),
        hours: Math.floor(6700 * progress),
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <Box
      ref={statsRef}
      sx={{
        py: { xs: 10, md: 14 },
        bgcolor: '#FFFFFF',
      }}
    >
      <Container>
        {/* Section Header - Modern Pattern */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'primary.main',
              mb: 2,
            }}
          >
            EREDMÉNYEINK
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              color: 'text.primary',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            WiseBox számokban
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4,
          }}
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const displayValue = stat.animationKey === 'revenue'
              ? animatedValues.revenue
              : stat.animationKey === 'users'
              ? animatedValues.users
              : animatedValues.hours;

            return (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  p: 4,
                  bgcolor: 'background.paper',
                  borderRadius: '12px',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Icon
                  sx={{
                    fontSize: '48px',
                    color: 'primary.main',
                    mb: 3,
                  }}
                />
                <Typography
                  sx={{
                    mb: 1,
                    color: 'text.primary',
                    fontSize: { xs: '32px', md: '36px' },
                    fontWeight: 700,
                  }}
                >
                  {displayValue.toLocaleString('hu-HU')}{stat.suffix}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    color: 'primary.main',
                    fontWeight: 600,
                    textTransform: 'lowercase',
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {stat.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};
